/**
 * Attribute store.
 *
 * Pinia store that holds the feed of attributes (spreadsheet-like columns)
 * and the variant rows built from them. A variant is one row of values,
 * one value per attribute column, and `currentVariantIndex` picks which
 * row is active right now.
 *
 * Used by the attribute panel, layer mapping, and anything that needs to
 * read or edit feed data and variants. The one thing to remember: layers
 * only store an attribute ID reference (`mappedAttributes`), so this store
 * never needs to know about layers directly - callers pass layers in when
 * they need a cross-reference (see `usedBy`).
 */
import { defineStore } from 'pinia';
import { createUid } from '@/utils/uid';

import type { Raw } from 'vue';
import type { StaticCanvas } from 'fabric';
import { LANGUAGE_KEY, PRODUCT_ID_KEY } from '@/constants/attributeValueTypes';
import type { AttributeValue, AttributeSourceType , Attribute } from '@/types/feed/attribute';

import type { LayerMap } from '@/types/layers/layer';
import type { AttributeFeedSource } from '@/types/library/template';
import type { Feed, FeedDetail } from '@/types/feed/feed';
import type { LanguageSourceMap, VariantOverrideMap } from '@/types/feed/variantOverrides';
import type { AttributeState, FeedListCache } from '@/types/stores/attribute';
import type { AttributeGroup } from '@/types/feed/attributeGroup';

export const useAttributeStore = defineStore('attribute', {
  state: (): AttributeState => ({
    attributes: {},
    variants: [],
    attributeGroups: [],
    variantIds: [],
    defaultVariantId: null,
    variantOverrides: {},
    languageSources: {},
    currentVariantIndex: 0,
    activeSourceType: null,
    feedLoading: false,
    feedError: null,
    source: null,
    activeVariantCanvas: null,
    feedId: null,
    feedName: null,
    feedListCache: null,
    feedDetail: null,
    feedLinkPending: false
  }),
  getters: {
    /**
     * Display order for every consumer — grid columns, the attribute panel,
     * the link picker, TSV copy. A Product ID or Language column is hoisted to
     * the front; everything else keeps its existing relative order, and
     * storage still goes through `attr.columnIndex`, which this never touches.
     */
    getAttributes: state => {
      const all = Object.values(state.attributes);
      const hoisted = all.filter(
        attr =>
          attr.targetKey === PRODUCT_ID_KEY || attr.targetKey === LANGUAGE_KEY
      );
      if (hoisted.length === 0) return all;
      return [...hoisted, ...all.filter(attr => !hoisted.includes(attr))];
    },
    /** The feed's Language column, when it has one. A feed will hold only one. */
    languageAttribute: state =>
      Object.values(state.attributes).find(
        attr => attr.targetKey === LANGUAGE_KEY
      ),
    /** The feed's Product ID column, when it has one. A feed will hold only one. */
    productIdAttribute: state =>
      Object.values(state.attributes).find(
        attr => attr.targetKey === PRODUCT_ID_KEY
      ),
    getAttributeById: state => (id: string | number) => {
      const targetId = String(id);
      if (state.attributes[targetId]) return state.attributes[targetId];
      if (state.attributes[id as string]) return state.attributes[id as string];

      for (const g of state.attributeGroups) {
        const found = g.attributes?.find(a => String(a.id) === targetId);
        if (found) return found;
      }
      return undefined;
    },
    totalVariants: state => state.variants.length,
    hasFeed: state =>
      Object.keys(state.attributes).length > 0 || state.attributeGroups.length > 0,
    ungroupedAttributes: (state): Attribute[] => Object.values(state.attributes),
    getAttributeValue:
      state =>
      (attr: Attribute): AttributeValue => {
        return (
          state.variants[state.currentVariantIndex]?.[attr.columnIndex] ?? ''
        );
      },
    /**
     * Returns layer IDs that map to a given attribute.
     * Requires layers to be passed since attribute store doesn't own layer state.
     */
    usedBy:
      () =>
      (attrId: string | number, layers: LayerMap): string[] => {
        const result: string[] = [];
        const targetId = String(attrId);
        for (const [layerId, layer] of Object.entries(layers)) {
          const ids = Object.values(layer.mappedAttributes ?? {});
          if (ids.some(id => String(id) === targetId)) {
            result.push(layerId);
          }
        }
        return result;
      }
  },
  actions: {
    setAttributes(attributes: Record<string, Attribute>) {
      this.attributes = attributes;
    },
    setAttribute(id: string, attr: Attribute) {
      this.attributes[id] = attr;
    },
    pushVariant(row: AttributeValue[], id?: string) {
      this.variants.push(row);
      this.variantIds.push(id ?? createUid());
    },
    spliceVariant(index: number) {
      this.variants.splice(index, 1);
      this.variantIds.splice(index, 1);
    },
    insertVariant(index: number, row: AttributeValue[], id?: string) {
      this.variants.splice(index, 0, row);
      this.variantIds.splice(index, 0, id ?? createUid());
    },
    moveVariant(fromIndex: number, toIndex: number) {
      const [row] = this.variants.splice(fromIndex, 1);
      if (!row) return;
      this.variants.splice(toIndex, 0, row);
      // Ids move with their row so overrides stay attached after a reorder.
      const [id] = this.variantIds.splice(fromIndex, 1);
      if (id) this.variantIds.splice(toIndex, 0, id);
    },
    setVariantIds(ids: string[]) {
      this.variantIds = [...ids];
    },
    /**
     * Makes `variantIds` line up with `variants` — trims extras and mints an
     * id for every row missing one. Safe to call after any wholesale load, so
     * feeds and templates saved before row identity existed get backfilled.
     */
    ensureVariantIds() {
      if (this.variantIds.length > this.variants.length) {
        this.variantIds.length = this.variants.length;
      }
      for (let i = 0; i < this.variants.length; i++) {
        if (!this.variantIds[i]) this.variantIds[i] = createUid();
      }
    },
    /**
     * Not undoable, and deliberately so: this is a cache the next translation
     * re-derives, not something the user authored.
     */
    setLanguageSource(variantId: string, attributeId: string, code: string) {
      const row = this.languageSources[variantId] ?? {};
      row[attributeId] = code;
      this.languageSources[variantId] = row;
    },
    /**
     * Forgets what a row's text was written in. The record describes a specific
     * piece of text, so once that text is replaced it is about something that
     * is no longer there — and a stale one is worse than none, because a target
     * matching it skips the call that would have caught the change.
     */
    clearLanguageSource(variantId: string, attributeId: string) {
      const row = this.languageSources[variantId];
      if (!row) return;
      delete row[attributeId];
      if (Object.keys(row).length === 0) delete this.languageSources[variantId];
    },
    setVariantCell(
      variantIndex: number,
      colIndex: number,
      value: AttributeValue
    ) {
      const row = this.variants[variantIndex];
      if (row) row[colIndex] = value;
    },
    appendToAllVariants(value: AttributeValue) {
      for (const row of this.variants) {
        row.push(value);
      }
    },
    removeAttribute(id: string) {
      delete this.attributes[id];
      const targetId = String(id);
      for (const g of this.attributeGroups) {
        if (g.attributes) {
          g.attributes = g.attributes.filter(a => String(a.id) !== targetId);
        }
      }
    },
    setAttributeGroups(attributeGroups: AttributeGroup[]) {
      this.attributeGroups = attributeGroups;
    },
    setSource(source: AttributeFeedSource | null) {
      this.source = source;
    },
    setFeedIdentity(feedId: number | null, feedName: string | null) {
      this.feedId = feedId;
      this.feedName = feedName;
    },
    setFeedDetail(detail: FeedDetail | null) {
      this.feedDetail = detail;
    },
    updateFeedListCache(patch: Partial<FeedListCache>) {
      if (!this.feedListCache) {
        this.feedListCache = {
          feeds: [],
          total: 0,
          nextPage: 1,
          done: false,
          isCoreTemplate: false
        };
      }
      Object.assign(this.feedListCache as FeedListCache, patch);
    },
    clearFeedListCache() {
      this.feedListCache = null;
    }
  }
});
